import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Upload, Download, FileText } from "lucide-react";
import { bulkUploadApi } from '../api/bulkUploadApi';
import BulkUploadErrorModal from './BulkUploadErrorModal';
import BulkUploadSuccessModal from "./BulkuploadSuccessModal";
import Cookies from "js-cookie";
import BulkUploadLoadingModal from "./BulkUploadLoadingModal";
import BulkUpdateModal from "./BulkUpdateModal";

const BulkUploadExcelModal = ({ 
  onClose, 
  onUpload, 
  uploadedFiles = [],
  setUploadedFiles, 
  officeCode,

}) => {
  const fileInputRef = useRef();
  const [errorMessage, setErrorMessage] = useState([]);
  const [skippedFiles, setSkippedFiles] = useState([]);
  const [contractsRateUpdated, setContractsRateUpdated] = useState([]);
  const [errorRows, setErrorRows] = useState([]);
  const [showUpddateSuccessModal, setUpdateShowSuccessModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);  
  const [isUploading, setIsUploading] = useState(false);

  const handleFilePick = (event) => {
    const files = Array.from(event.target.files);

    const enriched = files.map((file) => ({
      name: file.name,
      carrier: "CarrierX",
      user: "Current User",
      status: "Uploaded",
      raw: file,
    }));

    // setUploadedFiles((prev) => [...prev, ...enriched]);
    setUploadedFiles(enriched);
    event.target.value = '';
  };

  const handleCreateContracts = async () => {
    try {
      const file = uploadedFiles[0]?.raw;
      
      if (!file) {
        const noFileError = [{
          rowNumber: null,
          errorMessage: 'No file selected'
        }];
        setErrorMessage(noFileError);
        setShowErrorModal(true);
        return;
      }

      setIsUploading(true);

      //await new Promise(resolve => setTimeout(resolve, 15000));

      const officeCodesCookie = Cookies.get("officeCodes");
      const officeCodes = officeCodesCookie ? officeCodesCookie.split(",") : [];

      const response = await bulkUploadApi.uploadContractsExcel(officeCodes, file);
      
      if (response.data?.errorDetails?.length === 0) {
        setShowSuccessModal(true);
      } else {
        setErrorMessage(response.data.errorDetails);
        setShowErrorModal(true);
      }
      setUploadedFiles([]);
      
    } catch (error) {
      const systemError = [{
        rowNumber: null,
        errorMessage: error?.response?.data?.message || "Failed to upload contracts",
        timestamp: new Date().toISOString()
      }];
      setErrorMessage(systemError);
      setShowErrorModal(true);
    }
    finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
  setUploadedFiles((prev) =>
    prev.filter((_, index) => index !== indexToRemove)
  );

  // Also clear the file input so the same file can be re‑selected
  if (fileInputRef.current) {
    fileInputRef.current.value = "";
  }
};

  const handleUpdateContracts = async () => {
  try {
    const file = uploadedFiles[0]?.raw;
    if (!file) {
      const noFileError = [{
        rowNumber: null,
        errorMessage: 'No file selected',
        timestamp: new Date().toISOString()
      }];
      setErrorMessage(noFileError);
      setShowErrorModal(true);
      return;
    }

    setIsUploading(true);

    const officeCodesCookie = Cookies.get("officeCodes");
    const officeCodes = officeCodesCookie ? officeCodesCookie.split(",") : [];

    const { data } = await bulkUploadApi.updateContractsExcel(officeCodes, file);

    const successCount = data.successCount || 0;
    const skipDetails  = data.skipDetails || [];
    const errorDetails = data.errorDetails || [];

    setContractsRateUpdated(successCount);
    setSkippedFiles(skipDetails);
    setErrorRows(errorDetails);
    setUpdateShowSuccessModal(true);

    setUploadedFiles([]);
  } catch (error) {
    const systemError = [{
      rowNumber: null,
      errorMessage: error?.response?.data?.message || "Failed to update rates",
      timestamp: new Date().toISOString()
    }];
    setErrorMessage(systemError);
    setShowErrorModal(true);
  } finally {
    setIsUploading(false);
  }
};

  const handleCloseAll = () => {
    setShowErrorModal(false);
    setShowSuccessModal(false);
    setIsUploading(false);
    setErrorMessage([]);
    setUploadedFiles([]);
    onClose();
  };

  const handleViewContracts = () => {
    handleCloseAll();
    window.location.reload(); // refresh contracts page
  };

  return (
    <>
      <motion.div
        className="fixed inset-0 z-[1000] bg-black bg-opacity-40 flex items-center justify-center px-fluid-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-xl w-full max-w-4xl p-fluid-4 space-y-fluid-4"
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
        >
          {/* Header with Icon */}
          <div className="flex flex-col items-center space-y-fluid-1.5">
            <div className="bg-custom-bg-blue rounded-full p-fluid-2 text-white">
              <Upload size={32} />
            </div>
            <h2 className="text-fluid-2xl font-semibold text-[#111827] text-center">
              Upload Excel file to create a new contract
            </h2>
          </div>

          {/* Template + Upload UI */}
          <div className="bg-[#e7e9fb] rounded-xl px-fluid-4 py-fluid-3 flex flex-col items-center gap-fluid-3">
            <div className="flex items-center gap-fluid-2">
              <Download className="text-custom-bg-blue" size={18} />
              <a
                href="/templates/Contracts_Upload_Template.xlsx"
                download
                className="text-fluid-sm text-custom-bg-blue underline font-medium hover:text-[#1b2c7a]"
              >
                Template
              </a>
            </div>
            <button
              disabled={isUploading}
              onClick={() => fileInputRef.current?.click()}
              className="bg-custom-bg-blue text-white rounded-full px-fluid-5 py-fluid-2 text-fluid-sm font-medium hover:bg-[#1a2a61] transition"
            >
              Upload document
            </button>
            <input
              type="file"
              ref={fileInputRef}
              accept=".xlsx, .xls"
              onChange={handleFilePick}
              className="hidden"
            />
          </div>

          {/* File Preview Table */}
          <div className="rounded-xl overflow-hidden border border-gray-200">
           <div className="grid grid-cols-[2fr_1fr_auto] bg-custom-bg-blue text-white text-fluid-sm font-semibold px-fluid-3 py-fluid-2 items-center">
            <div>Document name</div>
            <div>User upload</div>
            <div className="text-right pr-2">Action</div>
          </div>

            {uploadedFiles.length > 0 ? (
              uploadedFiles.map((file, i) => (                  
                <div
                  key={i}
                  className="grid grid-cols-[2fr_1fr_auto] text-fluid-sm px-fluid-3 py-fluid-2 bg-[#f5f6fc] border-t border-gray-100 items-center"
                >
                  {/* Document name */}                  
                  <div className="flex items-center gap-2 truncate">
                    <FileText size={16} className="text-gray-500 shrink-0" />
                    <span className="truncate">{file.name}</span>
                  </div>

                  {/* User */}
                  <div className="truncate">{file.user}</div>

                  {/* Action */}
                  <div className="flex justify-end pr-2">
                    <button
                      onClick={() => handleRemoveFile(i)}
                      className="text-red-500 text-xs font-medium hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>

                </div>
              ))
            ) : (
              <div className="text-center text-fluid-sm text-gray-500 py-fluid-4 bg-[#f5f6fc]">
                Add Excel file to continue
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm transition"
            >
              Cancel
            </button>
           {/* <button
              disabled={uploadedFiles.length === 0 || isUploading}
              onClick={handleCreateContracts}
              className="px-fluid-button-x py-fluid-button-y bg-custom-bg-blue text-white hover:bg-[#1a2a61] text-fluid-sm rounded font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Create contract(s)"}
            </button> */}

             <button
              disabled={uploadedFiles.length === 0 || isUploading}
              onClick={handleUpdateContracts}
              className="px-fluid-button-x py-fluid-button-y bg-custom-bg-blue text-white hover:bg-[#1a2a61] text-fluid-sm rounded font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUploading ? "Uploading..." : "Create/Update Contract(s)"}
            </button>

          </div>
        </motion.div>
      </motion.div>
      {showErrorModal && (
        <BulkUploadErrorModal
          onClose={handleViewContracts} 
          errorMsgs={errorMessage}
        />
      )}
      {showSuccessModal && (
        <BulkUploadSuccessModal
          onClose={handleCloseAll}
          onViewContracts={handleViewContracts}
        />
      )}
       {showUpddateSuccessModal && (
        <BulkUpdateModal
          onClose={handleCloseAll}
          onViewContracts={handleViewContracts}
          contractsUpdated={contractsRateUpdated}
          skippedContracts={skippedFiles}
          errorFiles={errorRows}
        />
      )}
      {isUploading && (
        <BulkUploadLoadingModal onClose={handleCloseAll} />
      )}
    </>
  );
};

export default BulkUploadExcelModal;
